import { useQuery } from "@tanstack/react-query";
import { fetchClient } from "../../API/fetchClient";

// Common filter parameters for KPI analytics
export interface KpisFilters {
  from_period: string; // Format: "YYYY-MM"
  to_period: string; // Format: "YYYY-MM"
  site_id?: number | null;
  site_ids?: number[]; // Array of site IDs for multi-select
  bu_id?: number | null;
  bu_ids?: number[]; // Array of BU IDs for multi-select
  activite_id?: number | null;
  activite_ids?: number[]; // Array of activite IDs for multi-select
  filiere_id?: number | null;
  filiere_ids?: number[]; // Array of filiere IDs for multi-select
  region_id?: number | null;
  region_ids?: number[]; // Array of region IDs for multi-select
}

// Eau-specific filter with dimension_type
export interface EauKpisFilters extends KpisFilters {
  dimension_type: "produit" | "famille" | "rapport_surface";
}

// KPI summary item (from backend)
export interface KpiSummaryItem {
  code: string;
  name: string;
  total: number;
  avg: number;
  unit: string;
}

// Time series entry with dynamic KPI codes as keys
export interface KpiTimeSeriesEntry {
  month: string;
  [kpiCode: string]: string | number; // month is string, others are number
}

// Response structure for KPI analytics endpoints
export interface KpisAnalyticsResponse {
  summary: KpiSummaryItem[];
  time_series: KpiTimeSeriesEntry[];
}

/**
 * Hook for fetching Energy KPIs from /core/analytics/energie/
 *
 * Returns KPIs for:
 * - ENERGIE_ELEC_TEP: Electricity consumption (TEP)
 * - ENERGIE_GASOIL_TEP: Gasoil consumption (TEP)
 * - ENERGIE_ESSENCE_TEP: Essence consumption (TEP)
 * - ENERGIE_FUEL_TEP: Fuel consumption (TEP)
 * - ENERGIE_AUTRES_TEP: Other energy consumption (TEP)
 */
export const useGetEnergieKpis = (filters: KpisFilters) => {
  return useQuery<KpisAnalyticsResponse, Error>({
    queryKey: ["energieKpis", filters],
    queryFn: async () => {
      console.log("[useGetEnergieKpis] Fetching energy KPIs with filters:", filters);

      // Filter out null/undefined values
      const cleanedFilters: Record<string, unknown> = {};
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          cleanedFilters[key] = value;
        }
      });

      const response = await fetchClient<KpisAnalyticsResponse>("/core/analytics/energie/", {
        method: "POST",
        body: cleanedFilters,
      });

      if (response.error) {
        console.error("[useGetEnergieKpis] API Error:", response.error);
        throw new Error(`Failed to fetch energy KPIs: ${JSON.stringify(response.error)}`);
      }

      if (!response.data) {
        console.warn("[useGetEnergieKpis] No data received");
        return { summary: [], time_series: [] };
      }

      console.log("[useGetEnergieKpis] Successfully fetched energy KPIs");
      return response.data;
    },
    enabled: !!filters.from_period && !!filters.to_period,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * Hook for fetching Water KPIs from /core/analytics/eau/
 *
 * Returns KPIs grouped by:
 * - dimension_type: "produit" -> EAU_PROD_* codes
 * - dimension_type: "famille" -> EAU_FAM_* codes
 */
export const useGetEauKpis = (filters: EauKpisFilters, enabled = true) => {
  return useQuery<KpisAnalyticsResponse, Error>({
    queryKey: ["eauKpis", filters],
    queryFn: async () => {
      console.log("[useGetEauKpis] Fetching eau KPIs with filters:", filters);

      // Filter out null/undefined values
      const cleanedFilters: Record<string, unknown> = {};
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          cleanedFilters[key] = value;
        }
      });

      const response = await fetchClient<KpisAnalyticsResponse>("/core/analytics/eau/", {
        method: "POST",
        body: cleanedFilters,
      });

      if (response.error) {
        console.error("[useGetEauKpis] API Error:", response.error);
        throw new Error(`Failed to fetch eau KPIs: ${JSON.stringify(response.error)}`);
      }

      if (!response.data) {
        console.warn("[useGetEauKpis] No data received");
        return { summary: [], time_series: [] };
      }

      console.log("[useGetEauKpis] Successfully fetched eau KPIs");
      return response.data;
    },
    enabled: enabled && !!filters.from_period && !!filters.to_period && !!filters.dimension_type,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

// Helper to extract specific KPI data from summary
export function getKpiByCode(summary: KpiSummaryItem[], code: string): KpiSummaryItem | undefined {
  return summary.find((item) => item.code === code);
}

// Energy KPI codes (matching backend constants)
export const ENERGIE_KPI_CODES = {
  ELEC: "ENERGIE_ELEC_TEP",
  GASOIL: "ENERGIE_GASOIL_TEP",
  ESSENCE: "ENERGIE_ESSENCE_TEP",
  FUEL: "ENERGIE_FUEL_TEP",
  AUTRES: "ENERGIE_AUTRES_TEP",
  SOLAIRE: "ENERGIE_SOLAIRE_TEP",
} as const;
